import { AgingProfile } from '@/types';

export const AGING_PROFILES: AgingProfile[] = [
  {
    id: 'young-raw',
    label: 'Young & Raw',
    ageRange: [18, 25],
    description: 'Untrained fire with natural power. The voice hasn\'t learned its limits yet — and that\'s the weapon.',
    traitModifiers: { cleanness: 8, projection: 5, grit: -5, rasp: -5, vibrato: -8, airiness: 5 },
    physicsPrompt: `{AGING-PHYSICS: YOUNG-RAW}
[Age_Behavior: Untrained vocal cord elasticity; Natural overtone brightness; Youthful breath capacity]
[Age_Timbre: High collagen density cords; Minimal wear scarring; Bright harmonic stack]
[Age_Character: Raw energy over technique; Occasional pitch instability adds authenticity; Unbridled emotional power]`,
    sunoTags: ['young voice', 'raw vocals', 'youthful energy', 'untrained power']
  },
  {
    id: 'prime-voice',
    label: 'Prime Voice',
    ageRange: [26, 35],
    description: 'Peak vocal machinery. Maximum control meets maximum power — the golden decade where technique and fire coexist.',
    traitModifiers: { projection: 8, cleanness: 5, vibrato: 5, speed: 3 },
    physicsPrompt: `{AGING-PHYSICS: PRIME-VOICE}
[Age_Behavior: Peak cord elasticity and control; Optimized breath-to-power ratio; Trained resonance placement]
[Age_Timbre: Balanced harmonic richness; Full frequency utilization; Controlled dynamic range]
[Age_Character: Technique-fire fusion; Confident delivery; Maximum vocal headroom]`,
    sunoTags: ['powerful vocals', 'prime voice', 'peak performance', 'controlled power']
  },
  {
    id: 'seasoned-veteran',
    label: 'Seasoned Veteran',
    ageRange: [36, 45],
    description: 'Road-tested pipes with earned character. The slight wear adds texture that no young voice can fake.',
    traitModifiers: { grit: 8, rasp: 5, vibrato: 5, projection: 3, cleanness: -5, airiness: -3 },
    physicsPrompt: `{AGING-PHYSICS: SEASONED-VETERAN}
[Age_Behavior: Developed cord nodule texture; Road-tested breath control; Experience-shaped resonance]
[Age_Timbre: Early harmonic thickening; Slight fundamental weight gain; Earned mid-range warmth]
[Age_Character: Story in every note; Tour-hardened endurance; Wisdom-weighted delivery]`,
    sunoTags: ['seasoned vocals', 'experienced voice', 'road-tested', 'mature power']
  },
  {
    id: 'weathered-authority',
    label: 'Weathered Authority',
    ageRange: [46, 55],
    description: 'Gravel-paved authority. The voice carries decades of smoke, stages, and stories in its fiber.',
    traitModifiers: { grit: 15, rasp: 12, lowEnd: 8, vibrato: 3, cleanness: -12, airiness: -5, speed: -5 },
    physicsPrompt: `{AGING-PHYSICS: WEATHERED-AUTHORITY}
[Age_Behavior: Thickened vocal cord mass; Reduced upper range flexibility; Deepened chest resonance]
[Age_Timbre: Gravel undertone; Reduced high-frequency shimmer; Rich low-mid warmth]
[Age_Character: Decade-earned authority; Gravitas in every syllable; Unshakeable confidence]`,
    sunoTags: ['weathered voice', 'authoritative vocals', 'gravel voice', 'deep character']
  },
  {
    id: 'grizzled-legend',
    label: 'Grizzled Legend',
    ageRange: [56, 65],
    description: 'The voice is a monument to survival. Cracks and breaks aren\'t flaws — they\'re battle scars that tell the story.',
    traitModifiers: { grit: 20, rasp: 18, lowEnd: 12, vibrato: -5, cleanness: -18, speed: -10, airiness: 8, projection: -5 },
    physicsPrompt: `{AGING-PHYSICS: GRIZZLED-LEGEND}
[Age_Behavior: Significant cord mass increase; Natural vocal breaks as character; Reduced range compensated by depth]
[Age_Timbre: Heavy rasp patina; Fundamental frequency drop; Breath-leak warmth]
[Age_Character: Living legend gravitas; Cracks tell stories; Every note earned through decades of living]`,
    sunoTags: ['grizzled voice', 'legendary vocals', 'aged rasp', 'veteran singer']
  },
  {
    id: 'ancient-wisdom',
    label: 'Ancient Wisdom',
    ageRange: [66, 99],
    description: 'Beyond technique, beyond power. What remains is pure truth — a voice stripped to its emotional bedrock.',
    traitModifiers: { grit: 15, rasp: 20, lowEnd: 5, vibrato: -10, cleanness: -20, speed: -15, airiness: 15, projection: -10 },
    physicsPrompt: `{AGING-PHYSICS: ANCIENT-WISDOM}
[Age_Behavior: Thinned cord edges with heavy mass; Pronounced breath component; Narrowed usable range]
[Age_Timbre: Papyrus-thin upper register; Deep root-note warmth; Natural tremolo from age]
[Age_Character: Pure emotional truth; Stripped-to-bedrock honesty; Wisdom that needs no volume]`,
    sunoTags: ['aged voice', 'ancient wisdom', 'elder vocals', 'weathered and wise']
  }
];

export function getAgingProfileById(id: string): AgingProfile | undefined {
  return AGING_PROFILES.find(a => a.id === id);
}
